import { useState } from "react";
import { X, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const TAG_REGEX = /^[a-zåäö0-9-]+$/;
const MAX_TAGS = 20;
const MAX_TAG_LENGTH = 32;

function normalizeTag(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

interface PlaceTagsEditorProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  /** Render without wrapping Card (for embedding inside another card) */
  inline?: boolean;
}

export function PlaceTagsEditor({ tags, onChange, disabled, inline }: PlaceTagsEditorProps) {
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    setError(null);
    const tag = normalizeTag(input);
    if (!tag) return;

    if (tag.length > MAX_TAG_LENGTH) {
      setError(`Max ${MAX_TAG_LENGTH} tecken per tag`);
      return;
    }
    if (!TAG_REGEX.test(tag)) {
      setError("Endast bokstäver, siffror och bindestreck");
      return;
    }
    if (tags.includes(tag)) {
      setError("Taggen finns redan");
      return;
    }
    if (tags.length >= MAX_TAGS) {
      setError(`Max ${MAX_TAGS} taggar per plats`);
      return;
    }

    onChange([...tags, tag]);
    setInput("");
  };

  const handleRemove = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const content = (
    <div className="space-y-3">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1 pr-1">
              {tag}
              <button
                type="button"
                onClick={() => handleRemove(tag)}
                disabled={disabled}
                className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                aria-label={`Ta bort ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Ny tag..."
          disabled={disabled || tags.length >= MAX_TAGS}
          className="max-w-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled || !input.trim() || tags.length >= MAX_TAGS}
        >
          <Plus className="h-4 w-4 mr-1" />
          Lägg till
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <p className="text-xs text-muted-foreground">
        {tags.length} / {MAX_TAGS} taggar
      </p>
    </div>
  );

  if (inline) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Taggar</p>
        <p className="text-xs text-muted-foreground">Exempel: gårdsbutik, fäbod, kurs, natur</p>
        {content}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Taggar</CardTitle>
        <CardDescription>
          Exempel: gårdsbutik, fäbod, kurs, natur, mathantverk
        </CardDescription>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}
